import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { CmicContext } from '../../../context/CmicContext';
import { fetchFileType, isBGClrDark } from '../../../utility/UtilityFunctions';
import { COLORS } from '../../../utility/tasks/colors';
import { getInitials } from '@fluentui/react';
import { THEME_ID, baseFontFamilyStyle } from '../../../shared/styles';

import './CustomCell.css';
import { FILE_TYPE } from '../../../shared/Constants';

const CustomName = React.memo(({ params }) => {
	const initials = getInitials(params.value);
	if (!params.value) return '-';
	return (
		<Box className="custom-name">
			<Box id="custom-name_circle">{initials}</Box>
			<Box id="custom-name_full_name">{params.value}</Box>
		</Box>
	);
});

const CustomCell = React.memo(
	({ params, textStyle = { padding: '0 0.1rem 0 0' }, alignment, handleCellPress, showFileType }) => {
		const { currentOfficeTheme } = React.useContext(CmicContext);

		const wrapper = React.useRef(null);
		const cellDiv = React.useRef(null);
		const cellValue = React.useRef(null);
		const [showFullCell, setShowFullCell] = React.useState(false);

		const handleFRONTCLR = () => {
			const theme = currentOfficeTheme?.themeId ? currentOfficeTheme?.themeId : THEME_ID.WHITE;

			switch (theme) {
				case THEME_ID.WHITE:
				case THEME_ID.COLORFUL:
					return COLORS.OUTLOOK_BLACK_FONT;
				default:
					return currentOfficeTheme.bodyForegroundColor;
			}
		};

		const handleMouseEnter = () => {
			setShowFullCell(true);
		};

		const handleMouseLeave = () => {
			setShowFullCell(false);
		};

		React.useEffect(() => {
			if (!showFullCell) {
				return undefined;
			}
			function handleKeyDown(nativeEvent) {
				// IE11, Edge (prior to using Bink?) use 'Esc'
				if (nativeEvent.key === 'Escape' || nativeEvent.key === 'Esc') {
					setShowFullCell(false);
				}
			}

			document.addEventListener('keydown', handleKeyDown);

			return () => {
				document.removeEventListener('keydown', handleKeyDown);
			};
		}, [setShowFullCell, showFullCell]);

		let defaultProps = {
			boxShadow: 'none',
			textTransform: 'none',
			fontSize: 14,
			fontFamily: baseFontFamilyStyle,
			fontWeight: 600,
			whiteSpace: 'nowrap',
			margin: 0,
			paddingHorizontal: '1%',
			textOverflow: 'ellipsis',
			display: 'block',
			color: handleFRONTCLR(),
			width: '100%',
			cursor: 'default',
			textAlign: alignment ? alignment : 'end',
			textOverflow: 'ellipsis',
			whiteSpace: 'nowrap',
			overflow: 'hidden',
		};

		const pressabelProps = {
			'&:hover': {
				color: isBGClrDark() ? COLORS.OUTLOOK_LIGHT_BLUE : COLORS.OUTLOOK_DARK_BLUE,
				boxShadow: 'none',
				fontSize: '14px',
				fontFamily: baseFontFamilyStyle,
				textDecoration: 'underline',
				cursor: 'pointer',
			},
			'&:active': {
				boxShadow: 'none',
				color: '#0062cc',
			},
			'&:focus': {
				boxShadow: '0 0 0 0.2rem rgba(0,123,255,.5)',
			},
		};
		if (handleCellPress) {
			defaultProps = {
				...defaultProps,
				...pressabelProps,
			};
		}

		let valUI;

		if (showFileType) {
			const type = fetchFileType(params?.value);

			if (type === FILE_TYPE.Image) {
				valUI = FILE_TYPE.Image;
			} else if (type === FILE_TYPE.Pdf) {
				valUI = FILE_TYPE.Pdf;
			} else if (type === FILE_TYPE.Text) {
				valUI = FILE_TYPE.Text;
			} else {
				valUI = FILE_TYPE.Other;
			}
		} else {
			valUI = params?.value || '-';
		}

		return (
			<Box
				ref={wrapper}
				onMouseEnter={handleMouseEnter}
				onMouseLeave={handleMouseLeave}
				sx={{
					width: '100%',
					height: '100%',
					position: 'relative',
					display: 'inline-flex',
				}}
			>
				<Box
					ref={cellDiv}
					sx={{
						height: '100%',
						width: params.colDef?.computedWidth,
						display: 'block',
						position: 'absolute',
						top: 0,
						left: 0,
						alignItems: 'center',
					}}
				/>
				<Button
					ref={cellValue}
					sx={defaultProps}
					style={textStyle}
					onClick={
						handleCellPress
							? event => {
								event.stopPropagation();
								handleCellPress(params);
							}
							: null
					}
				>
					{valUI}
				</Button>
			</Box>
		);
	},
);

export default CustomCell;
